<template>
  <div class="connector-actions">
    <template v-if="!hideActions">
      <UIButtonIcon
        v-if="canDelete"
        v-tooltip="'Delete'"
        :size
        :leading="TrashIcon"
        variant="danger"
        fill="light"
        :disabled
        data-refid="connectorDeleteButton"
        @click="handleDelete"
      />
      <UIButtonIcon
        v-tooltip="'Rename'"
        :size
        :leading="PencilIcon"
        variant="secondary"
        fill="light"
        :disabled
        @click="handleEdit"
      />
    </template>
    <UIButtonSync
      v-if="isManual"
      :size
      :disabled="syncDisabled"
      :loading="disabled"
      label="Import"
      variant="secondary"
      @click="handleImport"
    />
    <template v-else>
      <UIButtonSync
        v-if="isConnectable"
        v-tooltip="'Sync'"
        :size
        :leading="ArrowPathIcon"
        :disabled="syncDisabled"
        :loading="disabled"
        variant="secondary"
        @click="handleSync"
      />
      <UIButton :size :disabled label="Connect" @click="handleConnect" />
    </template>
  </div>
  <Teleport :to="deleteContainer">
    <div v-if="deleteMode" class="connector-actions__confirm" @click.stop>
      <template v-if="!isUsedInOtherRepositories">
        Are you sure you want to delete the connector '{{ connector.name }}'?
      </template>
      <template v-else>
        You are about to delete the connector '{{ connector.name }}', which is
        shared across multiple repositories, including
        {{ displayRepositoriesExcludeCurrent }}. Deleting this connector will
        permanently remove it from all repositories where it is used, including
        '{{ repositoriesStore.getCurrentName }}'.<br /><br />Are you sure you
        want to proceed?
      </template>
      <div class="connector-actions__confirm-buttons">
        <UIButton
          label="Yes"
          variant="danger"
          size="xs"
          data-refid="connectorDeleteConfirmButton"
          @click="handleDeleteConfirm"
        />
        <UIButton
          label="No"
          variant="secondary"
          size="xs"
          @click="handleDeleteCancel"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, markRaw, ref, watchEffect } from 'vue'

import { ButtonSize, LinkedDataConnector, LinkedDataManualLoader, ReadonlyMode } from '@types'

import { NOTIFICATION_DELAY, OWNER_MODE } from '@/const'

import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'

import { handleCatchedError } from '@/helpers/common'
import { connectAccount } from '@/views/LinkedData/Connectors/components/utils/helpers'

import { useNotifications } from '@/plugins/notification'

import { useAssetsStore } from '@/store/assets/index'
import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useUserStore } from '@/store/user'

import { ArrowPathIcon, PencilIcon, TrashIcon } from '@heroicons/vue/24/outline'

import { UIButton, UIButtonIcon, UIButtonSync } from '@ui/buttons'
import AddAccountOFX from '@/views/LinkedData/Connectors/components/AddAccountOFX.vue'
import AddAccountAkoya from '@/views/LinkedData/Connectors/components/AddAccountAkoya.vue'

type Props = {
  connector: LinkedDataConnector
  hide?: () => void
  size?: ButtonSize
  hideActions?: boolean
  deleteContainer?: string
  isConnectable?: boolean
}

type Emits = {
  deleted: [data: string]
  edit: [data: string]
}

const { deleteContainer = 'body', ...props } = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsStore = useAssetsStore()
const linkedDataConnectorsStore = useLinkedDataConnectorsStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const userStore = useUserStore()

const { progress, remove, update } = useNotifications()
const {
  resync,
  syncing: isSyncInProgress,
  syncingConnectors,
} = useLinkedDataConnector()

const { plaid: handlePlaid, yodlee: handleYodlee } = useLinkedDataConnector()

const isOwner = inject<ReadonlyMode>(OWNER_MODE)

const internalLoading = ref(false)

const deleting = ref(false)
const deleteMode = ref(false)

const disabled = computed(() => internalLoading.value || syncing.value)

const connectorId = computed(() => props.connector.id)

const isManual = computed(() => props.connector.access_method === 'manual')
const connectorSource = computed(() => props.connector.source.toLowerCase())

const isConnectorCreator = computed(() => props.connector.created_by === userStore.getUserId)

const canDelete = computed(() => isConnectorCreator.value || isOwner?.value)

const syncing = computed(
  () =>
    !!connectorSource.value &&
    syncingConnectors.value.includes(connectorSource.value),
)

const syncDisabled = computed(
  () => disabled.value || isSyncInProgress.value,
)

const isUsedInOtherRepositories = computed(
  () => props.connector.repositories?.length > 1,
)

const displayRepositoriesExcludeCurrent = computed(() =>
  props.connector.repositories
    .filter(item => item.id !== repositoriesStore.getCurrentId)
    .map(item => `'${item.name}'`)
    .join(', '),
)

const handleImport = () => {
  if (syncDisabled.value) return

  switch (connectorSource.value as LinkedDataManualLoader) {
    case LinkedDataManualLoader.OFX:
      const modalInstance = modalsStore.init(
        'add-account-ofx-dialog',
        markRaw(AddAccountOFX),
      )
      modalInstance?.open(modalsStore.getZIndex(), {})
      break
  }
}

const handleConnect = () => {
  internalLoading.value = true
  switch (props.connector.access_method) {
    case 'plaid':
      handlePlaid(props.connector.id)
      break
    case 'yodlee':
      handleYodlee()
      break
    case 'akoya':
      connectAccount(
        props.connector.access_method,
        {
          name: props.connector.name,
          institution_id: props.connector.source,
          logo: props.connector.logo,
        }
      ).then(({ name, url, logo }) => {
        const modalInstance = modalsStore.init(
          'add-account-akoya-dialog',
          markRaw(AddAccountAkoya),
        )
        modalInstance?.open(modalsStore.getZIndex(), {
          name,
          url,
          logo,
        })
      }).catch(() => {
        internalLoading.value = false
      })
      break
    default: {
      connectAccount(props.connector.access_method, {
        name: props.connector.name,
        institution_id: props.connector.source,
        logo: props.connector.logo,
      }).then(({ url }) => {
        location.href = url
      }).catch(() => {
        internalLoading.value = false
      })
    }
  }
}

const handleSync = async () => {
  if (syncDisabled.value) return

  internalLoading.value = true

  try {
    await resync(connectorId.value)
  } finally {
    if (!syncing.value) internalLoading.value = false
  }
}

const handleEdit = () => {
  emit('edit', connectorId.value)
  props.hide?.()
}

const handleDelete = () => {
  deleteMode.value = true
}

const handleDeleteConfirm = async () => {
  deleting.value = true
  const nid = await progress({
    message: 'Deleting connector',
  })
  try {
    await linkedDataConnectorsStore.destroy(connectorId.value)
    await assetsStore.fetch()
    emit('deleted', connectorId.value)
    if (nid) {
      await update(
        nid,
        {
          type: 'success',
          message: 'Connector deleted',
        },
        NOTIFICATION_DELAY,
      )
    }
  } catch (e) {
    if (nid) await remove(nid)
    handleCatchedError(e as string, { id: connectorId.value })
  }
}

const handleDeleteCancel = () => {
  deleteMode.value = false
}

watchEffect(() => {
  internalLoading.value = syncing.value
})
</script>

<style>
.connector-actions {
  @apply flex items-center justify-end;
  @apply gap-1.5;

  &__confirm {
    @apply absolute inset-0;
    @apply flex flex-col items-center;
    @apply gap-4 p-4;
    @apply bg-white/95!;
    @apply dark:bg-body-dark/85!;
    @apply rounded-md;
    @apply text-sm sm:text-xs text-center;
    @apply cursor-default;
    @apply z-30;

    .ui-button {
      @apply min-w-12;
    }
  }

  &__confirm-buttons {
    @apply flex items-center;
    @apply gap-2;
  }
}
</style>
